import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CustomizationContext = createContext(null);

// Helper: Convert hex to HSL string for Shadcn variables
const hexToHSL = (hex) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  // Return HSL values as space-separated string (for CSS hsl() function)
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// ============================================
// PRESETS ET OPTIONS
// ============================================

// Polices disponibles
export const FONT_OPTIONS = [
  { id: 'inter', name: 'Inter', family: "'Inter', sans-serif", style: 'Moderne & Lisible' },
  { id: 'playfair', name: 'Playfair Display', family: "'Playfair Display', serif", style: 'Élégant & Classique' },
  { id: 'source-sans', name: 'Source Sans Pro', family: "'Source Sans Pro', sans-serif", style: 'Professionnel' },
  { id: 'roboto-mono', name: 'Roboto Mono', family: "'Roboto Mono', monospace", style: 'Technique' },
  { id: 'merriweather', name: 'Merriweather', family: "'Merriweather', serif", style: 'Classique & Élégant' },
];

// Palettes de couleurs
export const COLOR_PALETTES = [
  {
    id: 'ocre-rouge',
    name: 'Ocre & Rouge',
    colors: {
      primary: '#b91c1c',      // Rouge plus profond pour meilleur contraste
      secondary: '#dc2626',
      background: '#fef7f0',
      backgroundAlt: '#fde6cc',
      card: '#ffffff',
      text: '#1a1512',         // Texte plus foncé
      textMuted: '#6b4423',    // Muted plus contrasté
      border: '#e8d4bc',       // Bordure plus visible
      accent: '#ea580c',
    }
  },
  {
    id: 'bleu-pro',
    name: 'Bleu Professionnel',
    colors: {
      primary: '#1e40af',
      secondary: '#3b82f6',
      background: '#f8fafc',
      backgroundAlt: '#e2e8f0',
      card: '#ffffff',
      text: '#0f172a',
      textMuted: '#475569',    // Muted plus foncé
      border: '#cbd5e1',
      accent: '#2563eb',
    }
  },
  {
    id: 'vert-nature',
    name: 'Vert Nature',
    colors: {
      primary: '#166534',
      secondary: '#22c55e',
      background: '#f0fdf4',
      backgroundAlt: '#dcfce7',
      card: '#ffffff',
      text: '#14532d',
      textMuted: '#3d6b2c',    // Muted plus contrasté
      border: '#86efac',
      accent: '#16a34a',
    }
  },
  {
    id: 'gris-elegant',
    name: 'Gris Élégant',
    colors: {
      primary: '#374151',
      secondary: '#6b7280',
      background: '#f9fafb',
      backgroundAlt: '#f3f4f6',
      card: '#ffffff',
      text: '#111827',
      textMuted: '#4b5563',    // Muted plus foncé
      border: '#d1d5db',       // Bordure plus visible
      accent: '#4b5563',
    }
  },
  {
    id: 'terre-bois',
    name: 'Terre & Bois',
    colors: {
      primary: '#78350f',
      secondary: '#a16207',
      background: '#fefce8',
      backgroundAlt: '#fef3c7',
      card: '#fffbeb',
      text: '#451a03',
      textMuted: '#92400e',
      border: '#fcd34d',
      accent: '#b45309',
    }
  },
  {
    id: 'acier-brosse',
    name: 'Métal Acier Brossé',
    colors: {
      primary: '#475569',
      secondary: '#64748b',
      background: '#f1f5f9',
      backgroundAlt: '#e2e8f0',
      card: '#f8fafc',
      text: '#1e293b',
      textMuted: '#64748b',
      border: '#94a3b8',
      accent: '#334155',
    }
  },
];

// Options de géométrie des boutons
export const BUTTON_SHAPES = [
  { id: 'rounded', name: 'Arrondi', radius: '8px' },
  { id: 'square', name: 'Carré', radius: '2px' },
  { id: 'pill', name: 'Pill', radius: '9999px' },
];

// Options d'épaisseur de bordure
export const BORDER_WIDTHS = [
  { id: 'none', name: 'Pas de bordure', width: '0px' },
  { id: 'fine', name: 'Fine', width: '1px' },
  { id: 'medium', name: 'Moyenne', width: '2px' },
  { id: 'thick', name: 'Épaisse', width: '3px' },
];

// Options d'ombres
export const SHADOW_STYLES = [
  { id: 'none', name: 'Aucune', shadow: 'none' },
  { id: 'light', name: 'Légère', shadow: '0 1px 3px rgba(0,0,0,0.1)' },
  { id: 'medium', name: 'Prononcée', shadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)' },
  { id: 'heavy', name: 'Portée', shadow: '0 10px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.1)' },
];

// Options d'espacement
export const SPACING_OPTIONS = [
  { id: 'compact', name: 'Compact', multiplier: 0.75 },
  { id: 'normal', name: 'Normal', multiplier: 1 },
  { id: 'spacious', name: 'Aéré', multiplier: 1.25 },
];

// Options de texture - avec patterns plus visibles
export const TEXTURE_OPTIONS = [
  { id: 'none', name: 'Lisse', texture: 'none', pattern: '' },
  { id: 'grain', name: 'Grain léger', texture: 'grain', pattern: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' },
  { id: 'steel', name: 'Acier brossé', texture: 'steel', pattern: 'repeating-linear-gradient(90deg, rgba(0,0,0,0.03) 0px, rgba(0,0,0,0.03) 1px, transparent 1px, transparent 3px), repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 20px)' },
  { id: 'paper', name: 'Papier', texture: 'paper', pattern: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\'%3E%3Cfilter id=\'paper\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.04\' numOctaves=\'5\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23paper)\'/%3E%3C/svg%3E")' },
  { id: 'concrete', name: 'Béton subtil', texture: 'concrete', pattern: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'concrete\'%3E%3CfeTurbulence type=\'turbulence\' baseFrequency=\'0.02\' numOctaves=\'3\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23concrete)\'/%3E%3C/svg%3E")' },
  { id: 'dots', name: 'Points', texture: 'dots', pattern: 'radial-gradient(circle, rgba(0,0,0,0.08) 1px, transparent 1px)' },
];

// Options de force du relief - valeurs augmentées pour plus de visibilité
export const RELIEF_OPTIONS = [
  { id: 'subtle', name: 'Subtil', opacity: 0.08 },
  { id: 'light', name: 'Léger', opacity: 0.15 },
  { id: 'medium', name: 'Moyen', opacity: 0.25 },
  { id: 'strong', name: 'Fort', opacity: 0.40 },
];

// Options de layout dashboard
export const LAYOUT_OPTIONS = [
  { id: 'classic', name: 'Grille classique', description: 'Disposition standard en grille régulière' },
  { id: 'bento', name: 'Bento asymétrique', description: 'Cards de tailles variées style bento' },
  { id: 'stacked', name: 'Cards empilées', description: 'Cards en colonne verticale' },
  { id: 'compact', name: 'Liste compacte', description: 'Liste dense avec moins d\'espace' },
];

// Options d'animation
export const ANIMATION_OPTIONS = [
  { id: 'none', name: 'Aucune', description: 'Performance maximale', duration: '0ms', easing: 'linear' },
  { id: 'subtle', name: 'Subtiles', description: 'Transitions rapides', duration: '150ms', easing: 'ease-out' },
  { id: 'smooth', name: 'Fluides', description: 'Animations douces', duration: '300ms', easing: 'ease-in-out' },
  { id: 'expressive', name: 'Expressives', description: 'Effets prononcés', duration: '400ms', easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
];

// Styles d'icônes disponibles
export const ICON_STYLES = [
  { 
    id: 'outline', 
    name: 'Linéaire', 
    description: 'Contours fins et élégants',
    strokeWidth: 1.5,
    fill: 'none',
    preview: ['Home', 'Users', 'Settings', 'Calendar']
  },
  { 
    id: 'outline-bold', 
    name: 'Linéaire épais', 
    description: 'Contours plus visibles',
    strokeWidth: 2.5,
    fill: 'none',
    preview: ['Home', 'Users', 'Settings', 'Calendar']
  },
  { 
    id: 'filled', 
    name: 'Rempli', 
    description: 'Icônes solides et modernes',
    strokeWidth: 0,
    fill: 'currentColor',
    preview: ['Home', 'Users', 'Settings', 'Calendar']
  },
  { 
    id: 'duotone', 
    name: 'Duo-tone', 
    description: 'Deux tons pour plus de profondeur',
    strokeWidth: 1.5,
    fill: 'currentColor',
    fillOpacity: 0.2,
    preview: ['Home', 'Users', 'Settings', 'Calendar']
  },
];

// Options de logo
export const LOGO_POSITIONS = [
  { id: 'left', name: 'À gauche', description: 'Logo aligné à gauche' },
  { id: 'center', name: 'Centré', description: 'Logo centré dans l\'en-tête' },
];

export const LOGO_SIZES = [
  { id: 'small', name: 'Petit', height: 32 },
  { id: 'medium', name: 'Moyen', height: 40 },
  { id: 'large', name: 'Grand', height: 48 },
];

// Configuration par défaut
const DEFAULT_CONFIG = {
  typography: {
    headingFont: 'inter',
    bodyFont: 'inter',
    headingColor: null, // null = use palette text color
    bodyColor: null,
  },
  colors: {
    palette: 'gris-elegant',
    customOverrides: {},
  },
  shapes: {
    buttonShape: 'rounded',
    borderWidth: 'fine',
    shadowStyle: 'light',
    borderColor: null,
    shadowColor: null,
  },
  spacing: 'normal',
  texture: {
    type: 'none',
    relief: 'subtle',
  },
  layout: 'classic',
  animation: 'smooth',
  icons: {
    style: 'outline',
  },
  branding: {
    logoUrl: null,
    logoCompactUrl: null,
    faviconUrl: null,
    companyName: 'ris+partenaires',
    logoPosition: 'left',
    logoSize: 'medium',
  },
};

// ============================================
// HELPER: Apply styles synchronously to CSS variables
// ============================================
const applyStylesSync = (configToApply) => {
  const root = document.documentElement;
  
  // Typography
  const headingFont = FONT_OPTIONS.find(f => f.id === configToApply.typography.headingFont);
  const bodyFont = FONT_OPTIONS.find(f => f.id === configToApply.typography.bodyFont);
  
  if (headingFont) root.style.setProperty('--font-heading', headingFont.family);
  if (bodyFont) root.style.setProperty('--font-body', bodyFont.family);
  
  // Colors - Apply ALL palette colors
  const palette = COLOR_PALETTES.find(p => p.id === configToApply.colors.palette);
  if (palette) {
    Object.entries(palette.colors).forEach(([key, value]) => {
      const override = configToApply.colors.customOverrides?.[key];
      const finalColor = override || value;
      root.style.setProperty(`--color-${key}`, finalColor);
      
      if (key === 'background') {
        document.body.style.backgroundColor = finalColor;
      }
    });
    
    // Shadcn HSL variables
    const primaryHSL = hexToHSL(configToApply.colors.customOverrides?.primary || palette.colors.primary);
    const borderHSL = hexToHSL(configToApply.colors.customOverrides?.border || palette.colors.border);
    const backgroundHSL = hexToHSL(configToApply.colors.customOverrides?.background || palette.colors.background);
    const cardHSL = hexToHSL(configToApply.colors.customOverrides?.card || palette.colors.card);
    const textHSL = hexToHSL(configToApply.colors.customOverrides?.text || palette.colors.text);
    const textMutedHSL = hexToHSL(configToApply.colors.customOverrides?.textMuted || palette.colors.textMuted);
    const accentHSL = hexToHSL(configToApply.colors.customOverrides?.accent || palette.colors.accent);
    
    root.style.setProperty('--primary', primaryHSL);
    root.style.setProperty('--primary-foreground', '0 0% 100%');
    root.style.setProperty('--border', borderHSL);
    root.style.setProperty('--input', borderHSL);
    root.style.setProperty('--ring', primaryHSL);
    root.style.setProperty('--background', backgroundHSL);
    root.style.setProperty('--foreground', textHSL);
    root.style.setProperty('--card', cardHSL);
    root.style.setProperty('--card-foreground', textHSL);
    root.style.setProperty('--popover', cardHSL);
    root.style.setProperty('--popover-foreground', textHSL);
    root.style.setProperty('--muted', borderHSL);
    root.style.setProperty('--muted-foreground', textMutedHSL);
    root.style.setProperty('--accent', accentHSL);
    root.style.setProperty('--accent-foreground', '0 0% 100%');
    
    // Neumorphic colors
    root.style.setProperty('--neumo-dark-base', `color-mix(in srgb, ${palette.colors.text} 85%, ${palette.colors.background} 15%)`);
    root.style.setProperty('--neumo-dark-shadow', `color-mix(in srgb, ${palette.colors.text} 70%, black 30%)`);
    root.style.setProperty('--neumo-dark-deep', `color-mix(in srgb, ${palette.colors.text} 50%, black 50%)`);
    root.style.setProperty('--neumo-dark-light', `color-mix(in srgb, ${palette.colors.text} 90%, ${palette.colors.background} 10%)`);
    
    // Table colors
    root.style.setProperty('--table-header-bg', `color-mix(in srgb, ${palette.colors.primary} 15%, ${palette.colors.background} 85%)`);
    root.style.setProperty('--table-row-alt-bg', `color-mix(in srgb, ${palette.colors.primary} 5%, ${palette.colors.background} 95%)`);
    root.style.setProperty('--table-border-color', `color-mix(in srgb, ${palette.colors.primary} 25%, ${palette.colors.background} 75%)`);
  }
  
  // Custom typography colors
  if (configToApply.typography.headingColor) {
    root.style.setProperty('--color-heading', configToApply.typography.headingColor);
  } else {
    root.style.removeProperty('--color-heading');
  }
  if (configToApply.typography.bodyColor) {
    root.style.setProperty('--color-body', configToApply.typography.bodyColor);
  } else {
    root.style.removeProperty('--color-body');
  }
  
  // Shapes
  const buttonShape = BUTTON_SHAPES.find(s => s.id === configToApply.shapes.buttonShape);
  const borderWidth = BORDER_WIDTHS.find(b => b.id === configToApply.shapes.borderWidth);
  const shadowStyle = SHADOW_STYLES.find(s => s.id === configToApply.shapes.shadowStyle);
  
  if (buttonShape) root.style.setProperty('--radius-button', buttonShape.radius);
  if (borderWidth) root.style.setProperty('--border-width', borderWidth.width);
  if (shadowStyle) root.style.setProperty('--shadow-card', shadowStyle.shadow);
  if (configToApply.shapes.borderColor) {
    root.style.setProperty('--color-border-custom', configToApply.shapes.borderColor);
    root.style.setProperty('--color-border', configToApply.shapes.borderColor);
  }
  if (configToApply.shapes.shadowColor) root.style.setProperty('--shadow-color', configToApply.shapes.shadowColor);
  
  // Spacing
  const spacing = SPACING_OPTIONS.find(s => s.id === configToApply.spacing);
  if (spacing) root.style.setProperty('--spacing-multiplier', spacing.multiplier);
  
  // Texture
  const texture = TEXTURE_OPTIONS.find(t => t.id === configToApply.texture.type);
  const relief = RELIEF_OPTIONS.find(r => r.id === configToApply.texture.relief);
  
  if (texture && texture.id !== 'none' && texture.pattern) {
    if (texture.id === 'dots') {
      root.style.setProperty('--texture-pattern', texture.pattern);
      document.body.style.setProperty('--texture-bg-size', '20px 20px');
    } else {
      root.style.setProperty('--texture-pattern', texture.pattern);
      document.body.style.setProperty('--texture-bg-size', '200px 200px');
    }
    root.style.setProperty('--texture-opacity', String(relief?.opacity || 0.15));
  } else {
    root.style.setProperty('--texture-pattern', 'none');
    root.style.setProperty('--texture-opacity', '0');
  }
  
  // Layout
  root.style.setProperty('--layout-mode', configToApply.layout);
  
  // Animation
  const animation = ANIMATION_OPTIONS.find(a => a.id === configToApply.animation);
  if (animation) {
    root.style.setProperty('--transition-duration', animation.duration);
    root.style.setProperty('--transition-easing', animation.easing);
  }
};

// Load and apply config synchronously BEFORE first render
const loadInitialConfig = () => {
  const saved = localStorage.getItem('appCustomization');
  console.log('[CustomizationContext] Loading from localStorage:', saved ? JSON.parse(saved).colors?.palette : 'not found');
  
  let initialConfig = DEFAULT_CONFIG;
  
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      initialConfig = {
        ...DEFAULT_CONFIG,
        ...parsed,
        typography: { ...DEFAULT_CONFIG.typography, ...parsed.typography },
        colors: { ...DEFAULT_CONFIG.colors, ...parsed.colors, palette: 'gris-elegant' },
        shapes: { ...DEFAULT_CONFIG.shapes, ...parsed.shapes },
        texture: { ...DEFAULT_CONFIG.texture, ...parsed.texture },
      };
    } catch {
      initialConfig = DEFAULT_CONFIG;
    }
  }
  
  // Apply styles SYNCHRONOUSLY before returning
  applyStylesSync(initialConfig);
  
  return initialConfig;
};

// ============================================
// PROVIDER
// ============================================

export const CustomizationProvider = ({ children }) => {
  
  // Version counter that increments on every config change to force re-renders
  const [configVersion, setConfigVersion] = useState(0);
  
  // Load config AND apply styles synchronously during initialization
  const [config, setConfig] = useState(() => loadInitialConfig());

  // Appliquer les styles globalement
  const applyStyles = useCallback(() => {
    const root = document.documentElement;
    
    // Typographie - avec vérifications de sécurité
    const typography = config.typography || DEFAULT_CONFIG.typography;
    const headingFont = FONT_OPTIONS.find(f => f.id === typography?.headingFont);
    const bodyFont = FONT_OPTIONS.find(f => f.id === typography?.bodyFont);
    
    if (headingFont) {
      root.style.setProperty('--font-heading', headingFont.family);
    }
    if (bodyFont) {
      root.style.setProperty('--font-body', bodyFont.family);
    }
    
    // Couleurs - Appliquer TOUTES les couleurs de la palette
    const palette = COLOR_PALETTES.find(p => p.id === config.colors.palette);
    if (palette) {
      Object.entries(palette.colors).forEach(([key, value]) => {
        const override = config.colors.customOverrides[key];
        const finalColor = override || value;
        root.style.setProperty(`--color-${key}`, finalColor);
        
        // Appliquer aussi au body pour le fond
        if (key === 'background') {
          document.body.style.backgroundColor = finalColor;
        }
      });
      
      // Also update Shadcn HSL variables for buttons, inputs, etc.
      const primaryHSL = hexToHSL(config.colors.customOverrides.primary || palette.colors.primary);
      const borderHSL = hexToHSL(config.colors.customOverrides.border || palette.colors.border);
      const backgroundHSL = hexToHSL(config.colors.customOverrides.background || palette.colors.background);
      const cardHSL = hexToHSL(config.colors.customOverrides.card || palette.colors.card);
      const textHSL = hexToHSL(config.colors.customOverrides.text || palette.colors.text);
      const textMutedHSL = hexToHSL(config.colors.customOverrides.textMuted || palette.colors.textMuted);
      const accentHSL = hexToHSL(config.colors.customOverrides.accent || palette.colors.accent);
      
      root.style.setProperty('--primary', primaryHSL);
      root.style.setProperty('--primary-foreground', '0 0% 100%');
      root.style.setProperty('--border', borderHSL);
      root.style.setProperty('--input', borderHSL);
      root.style.setProperty('--ring', primaryHSL);
      root.style.setProperty('--background', backgroundHSL);
      root.style.setProperty('--foreground', textHSL);
      root.style.setProperty('--card', cardHSL);
      root.style.setProperty('--card-foreground', textHSL);
      root.style.setProperty('--popover', cardHSL);
      root.style.setProperty('--popover-foreground', textHSL);
      root.style.setProperty('--muted', borderHSL);
      root.style.setProperty('--muted-foreground', textMutedHSL);
      root.style.setProperty('--accent', accentHSL);
      root.style.setProperty('--accent-foreground', '0 0% 100%');
      
      // Generate neumorphic colors derived from theme
      root.style.setProperty('--neumo-dark-base', `color-mix(in srgb, ${palette.colors.text} 85%, ${palette.colors.background} 15%)`);
      root.style.setProperty('--neumo-dark-shadow', `color-mix(in srgb, ${palette.colors.text} 70%, black 30%)`);
      root.style.setProperty('--neumo-dark-deep', `color-mix(in srgb, ${palette.colors.text} 50%, black 50%)`);
      root.style.setProperty('--neumo-dark-light', `color-mix(in srgb, ${palette.colors.text} 90%, ${palette.colors.background} 10%)`);
      
      // Table colors derived from theme
      root.style.setProperty('--table-header-bg', `color-mix(in srgb, ${palette.colors.primary} 15%, ${palette.colors.background} 85%)`);
      root.style.setProperty('--table-row-alt-bg', `color-mix(in srgb, ${palette.colors.primary} 5%, ${palette.colors.background} 95%)`);
      root.style.setProperty('--table-border-color', `color-mix(in srgb, ${palette.colors.primary} 25%, ${palette.colors.background} 75%)`);
    }
    
    // Couleurs typographie personnalisées
    if (typography?.headingColor) {
      root.style.setProperty('--color-heading', typography.headingColor);
    } else {
      root.style.removeProperty('--color-heading');
    }
    if (typography?.bodyColor) {
      root.style.setProperty('--color-body', typography.bodyColor);
    } else {
      root.style.removeProperty('--color-body');
    }
    
    // Formes - avec vérifications de sécurité
    const shapes = config.shapes || DEFAULT_CONFIG.shapes;
    const buttonShape = BUTTON_SHAPES.find(s => s.id === shapes?.buttonShape);
    const borderWidth = BORDER_WIDTHS.find(b => b.id === shapes?.borderWidth);
    const shadowStyle = SHADOW_STYLES.find(s => s.id === shapes?.shadowStyle);
    
    if (buttonShape) root.style.setProperty('--radius-button', buttonShape.radius);
    if (borderWidth) root.style.setProperty('--border-width', borderWidth.width);
    if (shadowStyle) root.style.setProperty('--shadow-card', shadowStyle.shadow);
    if (shapes?.borderColor) {
      root.style.setProperty('--color-border-custom', shapes.borderColor);
      root.style.setProperty('--color-border', shapes.borderColor);
    }
    if (shapes?.shadowColor) root.style.setProperty('--shadow-color', shapes.shadowColor);
    
    // Espacement
    const spacing = SPACING_OPTIONS.find(s => s.id === config.spacing);
    if (spacing) root.style.setProperty('--spacing-multiplier', spacing.multiplier);
    
    // Style d'icônes
    const iconStyle = ICON_STYLES.find(s => s.id === config.icons?.style) || ICON_STYLES[0];
    if (iconStyle) {
      root.style.setProperty('--icon-stroke-width', String(iconStyle.strokeWidth || 1.5));
      root.style.setProperty('--icon-fill', iconStyle.fill || 'none');
      root.style.setProperty('--icon-fill-opacity', String(iconStyle.fillOpacity || 0));
      // Add class to body for CSS-based icon styling
      document.body.classList.remove('icons-outline', 'icons-outline-bold', 'icons-filled', 'icons-duotone');
      document.body.classList.add(`icons-${iconStyle.id}`);
    }
    
    // Texture - avec vérifications de sécurité
    const textureConfig = config.texture || DEFAULT_CONFIG.texture;
    const texture = TEXTURE_OPTIONS.find(t => t.id === textureConfig?.type);
    const relief = RELIEF_OPTIONS.find(r => r.id === textureConfig?.relief);
    
    if (texture && texture.id !== 'none' && texture.pattern) {
      // Handle special case for dots pattern that needs background-size
      if (texture.id === 'dots') {
        root.style.setProperty('--texture-pattern', texture.pattern);
        document.body.style.setProperty('--texture-bg-size', '20px 20px');
      } else {
        root.style.setProperty('--texture-pattern', texture.pattern);
        document.body.style.setProperty('--texture-bg-size', '200px 200px');
      }
      root.style.setProperty('--texture-opacity', String(relief?.opacity || 0.15));
    } else {
      root.style.setProperty('--texture-pattern', 'none');
      root.style.setProperty('--texture-opacity', '0');
    }
    
    // Layout
    root.style.setProperty('--layout-mode', config.layout);
    
    // Animation
    const animation = ANIMATION_OPTIONS.find(a => a.id === config.animation);
    if (animation) {
      root.style.setProperty('--transition-duration', animation.duration);
      root.style.setProperty('--transition-easing', animation.easing);
    }
  }, [config]);

  // Appliquer au chargement et à chaque changement de config
  useEffect(() => {
    applyStyles();
    // Increment version to force re-render in consuming components
    setConfigVersion(v => v + 1);
    // Dispatch custom event for icon components to re-render
    window.dispatchEvent(new CustomEvent('customization-change'));
  }, [config, applyStyles]);

  // Update favicon dynamically when branding changes
  useEffect(() => {
    if (config.branding?.faviconUrl) {
      // Find existing favicon link or create new one
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = config.branding.faviconUrl;
      
      // Also update apple-touch-icon if exists
      let appleLink = document.querySelector("link[rel='apple-touch-icon']");
      if (appleLink) {
        appleLink.href = config.branding.faviconUrl;
      }
    }
  }, [config.branding?.faviconUrl]);

  // Gestion des thèmes sauvegardés (from backend)
  const [savedThemes, setSavedThemes] = useState([]);
  
  // Load saved themes from backend on mount
  useEffect(() => {
    const loadSavedThemes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/saved`);
        if (response.ok) {
          const themes = await response.json();
          setSavedThemes(themes);
        }
      } catch (error) {
        console.error('Error loading saved themes:', error);
        // Fallback to localStorage
        const saved = localStorage.getItem('savedCustomThemes');
        if (saved) {
          try {
            setSavedThemes(JSON.parse(saved));
          } catch {
            setSavedThemes([]);
          }
        }
      }
    };
    loadSavedThemes();
  }, []);

  // Sauvegarder le thème actuel (to backend)
  const saveCurrentTheme = async (themeName, token) => {
    const newTheme = {
      name: themeName,
      config: { ...config }
    };
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/saved`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newTheme)
      });
      
      if (response.ok) {
        const result = await response.json();
        setSavedThemes(prev => [...prev, result.theme]);
        return result.theme;
      } else {
        // Fallback to localStorage
        const localTheme = {
          id: `custom-${Date.now()}`,
          name: themeName,
          config: { ...config },
          createdAt: new Date().toISOString()
        };
        const updatedThemes = [...savedThemes, localTheme];
        setSavedThemes(updatedThemes);
        localStorage.setItem('savedCustomThemes', JSON.stringify(updatedThemes));
        return localTheme;
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      // Fallback to localStorage
      const localTheme = {
        id: `custom-${Date.now()}`,
        name: themeName,
        config: { ...config },
        createdAt: new Date().toISOString()
      };
      const updatedThemes = [...savedThemes, localTheme];
      setSavedThemes(updatedThemes);
      localStorage.setItem('savedCustomThemes', JSON.stringify(updatedThemes));
      return localTheme;
    }
  };

  // Charger un thème sauvegardé
  const loadSavedTheme = (themeId) => {
    const theme = savedThemes.find(t => t.id === themeId);
    if (theme) {
      setConfig(theme.config);
    }
  };

  // Supprimer un thème sauvegardé
  const deleteSavedTheme = async (themeId, token) => {
    if (!token) {
      console.error('No token provided for theme deletion');
      return false;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/saved/${themeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setSavedThemes(prev => prev.filter(t => t.id !== themeId));
        // Update localStorage only on success
        const updatedThemes = savedThemes.filter(t => t.id !== themeId);
        localStorage.setItem('savedCustomThemes', JSON.stringify(updatedThemes));
        return true;
      } else {
        console.error('Failed to delete theme:', response.status, await response.text());
        return false;
      }
    } catch (error) {
      console.error('Error deleting theme:', error);
      return false;
    }
  };

  // Mettre à jour une section de la config - with immediate localStorage save
  const updateConfig = useCallback((section, value) => {
    console.log('[CustomizationContext] updateConfig called:', section, value);
    setConfig(prev => {
      const newConfig = {
        ...prev,
        [section]: typeof value === 'object' ? { ...prev[section], ...value } : value
      };
      // Immediately save to localStorage to avoid race conditions
      localStorage.setItem('appCustomization', JSON.stringify(newConfig));
      console.log('[CustomizationContext] Saved to localStorage:', newConfig.colors.palette);
      return newConfig;
    });
  }, []);

  // Réinitialiser
  const resetConfig = useCallback(() => {
    setConfig(DEFAULT_CONFIG);
    localStorage.setItem('appCustomization', JSON.stringify(DEFAULT_CONFIG));
  }, []);

  // Obtenir la palette actuelle
  const getCurrentPalette = () => {
    return COLOR_PALETTES.find(p => p.id === config.colors.palette) || COLOR_PALETTES[0];
  };

  return (
    <CustomizationContext.Provider value={{
      config,
      updateConfig,
      resetConfig,
      getCurrentPalette,
      configVersion,
      // Gestion des thèmes sauvegardés
      savedThemes,
      saveCurrentTheme,
      loadSavedTheme,
      deleteSavedTheme,
      // Export des options pour les composants
      FONT_OPTIONS,
      COLOR_PALETTES,
      BUTTON_SHAPES,
      BORDER_WIDTHS,
      SHADOW_STYLES,
      SPACING_OPTIONS,
      TEXTURE_OPTIONS,
      RELIEF_OPTIONS,
      LAYOUT_OPTIONS,
      ANIMATION_OPTIONS,
    }}>
      {children}
    </CustomizationContext.Provider>
  );
};

export const useCustomization = () => {
  const context = useContext(CustomizationContext);
  if (!context) {
    throw new Error('useCustomization must be used within a CustomizationProvider');
  }
  return context;
};

// Helper function to get theme colors from CSS variables (for components that can't use hooks)
export const getThemeColors = () => {
  const root = document.documentElement;
  const getVar = (name, fallback) => {
    const value = getComputedStyle(root).getPropertyValue(name).trim();
    return value || fallback;
  };
  
  return {
    primary: getVar('--color-primary', '#ea580c'),
    secondary: getVar('--color-secondary', '#9a3412'),
    background: getVar('--color-background', '#fef7f0'),
    backgroundAlt: getVar('--color-backgroundAlt', '#fef3c7'),
    card: getVar('--color-card', '#ffffff'),
    text: getVar('--color-text', '#1a1412'),
    textMuted: getVar('--color-textMuted', '#8b7355'),
    border: getVar('--color-border', '#e8d5c4'),
    accent: getVar('--color-accent', '#b45309'),
  };
};
